import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/types/api.generated";
import type { ProgressUpdateStatus } from "@/types/entities";
import { toast } from "sonner";

export interface ProgressUpdateFilters {
  status?: ProgressUpdateStatus;
  cursor?: string;
  limit?: number;
}

export function useProgressUpdates(krId: string, filters: ProgressUpdateFilters = {}) {
  return useQuery({
    queryKey: ["progress-updates", krId, filters],
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/v1/key-results/{id}/progress-updates",
        {
          params: {
            path: { id: krId },
            query: filters,
          },
        },
      );
      if (error) throw new Error(error.error.message);
      return data;
    },
    enabled: !!krId,
  });
}

export function useProgressUpdate(krId: string, progressUpdateId: string) {
  return useQuery({
    queryKey: ["progress-updates", krId, progressUpdateId],
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/v1/key-results/{id}/progress-updates/{progress_update_id}",
        {
          params: {
            path: { id: krId, progress_update_id: progressUpdateId },
          },
        },
      );
      if (error) throw new Error(error.error.message);
      return data;
    },
    enabled: !!krId && !!progressUpdateId,
  });
}

export function useCreateProgressUpdate(krId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateProgressUpdateRequest"]) => {
      const { data, error } = await apiClient.POST(
        "/v1/key-results/{id}/progress-updates",
        {
          params: { path: { id: krId } },
          body,
        },
      );
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["progress-updates", krId] });
      toast.success("Progress update created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateProgressUpdate(krId: string, progressUpdateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["UpdateProgressUpdateRequest"]) => {
      const { data, error } = await apiClient.PUT(
        "/v1/key-results/{id}/progress-updates/{progress_update_id}",
        {
          params: {
            path: { id: krId, progress_update_id: progressUpdateId },
          },
          body,
        },
      );
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["progress-updates", krId] });
      toast.success("Progress update saved");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteProgressUpdate(krId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (progressUpdateId: string) => {
      const { error } = await apiClient.DELETE(
        "/v1/key-results/{id}/progress-updates/{progress_update_id}",
        {
          params: {
            path: { id: krId, progress_update_id: progressUpdateId },
          },
        },
      );
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["progress-updates", krId] });
      toast.success("Progress update deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useFinalizeProgressUpdate(krId: string, progressUpdateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.PATCH(
        "/v1/key-results/{id}/progress-updates/{progress_update_id}/status",
        {
          params: {
            path: { id: krId, progress_update_id: progressUpdateId },
          },
          body: { status: "finalized" },
        },
      );
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["progress-updates", krId] });
      toast.success("Progress update finalized");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
